import { apiBaseUrl, apiClient } from "@/services/apiClient";
import { getToken } from "@/services/authStorage";

export type SupportMessageAuthorKind =
  | "customer"
  | "agent"
  | "ai"
  | "system"
  | "agent_private";

export type SupportConversationStatus =
  | "open"
  | "pending"
  | "snoozed"
  | "closed";

export interface SupportMessage {
  id: string;
  authorKind: SupportMessageAuthorKind;
  authorUserId: string | null;
  /** First name of the person who wrote it. Null for the AI and the customer. */
  authorName: string | null;
  /** Their profile image URL, when they have one. */
  authorAvatar: string | null;
  body: string;
  createdAt: string;
}

export interface SupportConversation {
  id: string;
  status: SupportConversationStatus;
  unreadCount: number;
}

export interface SupportInboxItem {
  id: string;
  tenantId: string | null;
  userId: string | null;
  status: SupportConversationStatus;
  assigneeUserId: string | null;
  subject: string | null;
  /** Last customer-visible message, so the inbox is triageable without clicking. */
  preview: string | null;
  previewAuthorKind: string | null;
  lastCustomerMessageAt: string | null;
  lastAgentMessageAt: string | null;
  /** Null when the last word was ours — the ball is not in our court. */
  waitingSinceMs: number | null;
  /** Who you are answering: a visitor, a business customer, or an accountant. */
  audience: "visitor" | "customer" | "accountant";
  /** Company name, or the person's name when there is no company. */
  label: string;
}

/* ---------------------------------------------------------------- customer */

/**
 * The caller's open thread, or null. Does NOT create one — opening the
 * widget must not mint a conversation row.
 */
export function getCurrentSupportConversation(): Promise<SupportConversation | null> {
  return apiClient
    .get<SupportConversation | null>("/support/conversation")
    .then((res) => res.data);
}

export function openSupportConversation(): Promise<SupportConversation> {
  return apiClient
    .post<SupportConversation>("/support/conversation")
    .then((res) => res.data);
}

export interface SupportAvailability {
  open: boolean;
  message: string;
}

export function getSupportAvailability(): Promise<SupportAvailability> {
  return apiClient
    .get<SupportAvailability>("/support/availability")
    .then((res) => res.data);
}

export interface SupportThread {
  messages: SupportMessage[];
  /** When support last read the thread; null if never. */
  agentLastReadAt: string | null;
}

export function getSupportMessages(
  conversationId: string,
  before?: string
): Promise<SupportThread> {
  const query = before ? `?before=${encodeURIComponent(before)}` : "";
  return apiClient
    .get<SupportThread>(
      `/support/conversations/${conversationId}/messages${query}`
    )
    .then((res) => res.data);
}

export function postSupportMessage(
  conversationId: string,
  body: string
): Promise<SupportMessage> {
  return apiClient
    .post<SupportMessage>(`/support/conversations/${conversationId}/messages`, {
      body,
    })
    .then((res) => res.data);
}

/**
 * Ask for a person.
 *
 * The endpoint has existed since the AI responder shipped, but only the
 * landing widget ever called it — so an authenticated customer had no way
 * to say "the bot is not helping". That mattered more once an ordinary
 * message stopped emailing the operators: this is now the action that
 * interrupts a person.
 */
export function escalateSupportConversation(
  conversationId: string
): Promise<void> {
  return apiClient
    .post(`/support/conversations/${conversationId}/escalate`)
    .then(() => undefined);
}

export function markSupportConversationRead(
  conversationId: string
): Promise<void> {
  return apiClient
    .post(`/support/conversations/${conversationId}/read`)
    .then(() => undefined);
}

export interface SupportConversationSummary {
  id: string;
  status: SupportConversationStatus;
  unreadCount: number;
  /**
   * The company this thread is about. This list is scoped by USER, while the
   * live thread is scoped by (user, tenant) — so an accountant's threads span
   * several clients and a row needs this to say which. Null for a thread
   * opened with no tenant, or one whose company has since been deleted.
   */
  tenant: { id: string; name: string } | null;
  lastMessage: {
    body: string;
    authorKind: string;
    authorName: string | null;
    authorAvatar: string | null;
    createdAt: string;
  } | null;
}

/** The caller's threads, newest first. Empty ones are filtered server-side. */
export function listSupportConversations(): Promise<
  SupportConversationSummary[]
> {
  return apiClient
    .get<SupportConversationSummary[]>("/support/conversations")
    .then((res) => res.data);
}

export interface SupportTopic {
  slug: string;
  title: string;
}

export interface SupportTopicDetail extends SupportTopic {
  body: string;
  sourceUrl: string | null;
}

/**
 * Help-centre search. An empty or very short query returns the topic
 * INDEX rather than results, so browsing costs no embedding call.
 */
export function searchSupportKnowledge(
  query: string
): Promise<{ topics: SupportTopic[]; results: SupportTopicDetail[] }> {
  return apiClient
    .get<{ topics: SupportTopic[]; results: SupportTopicDetail[] }>(
      `/support/knowledge?q=${encodeURIComponent(query)}`
    )
    .then((res) => res.data);
}

export function getSupportTopic(slug: string): Promise<SupportTopicDetail> {
  return apiClient
    .get<SupportTopicDetail>(`/support/knowledge/${slug}`)
    .then((res) => res.data);
}

export interface SupportTeamMember {
  name: string | null;
  avatar: string | null;
}

export function getSupportTeam(): Promise<SupportTeamMember[]> {
  return apiClient
    .get<SupportTeamMember[]>("/support/team")
    .then((res) => res.data);
}

/* ------------------------------------------------------------------- agent */

export interface SupportMetrics {
  windowDays: number;
  conversationsOpened: number;
  awaitingReply: number;
  longestWaitingMinutes: number | null;
  medianFirstResponseMinutes: number | null;
  slaBreaches: number;
  aiAnsweredShare: number;
  messagesByAuthorKind: Array<{ authorKind: string; count: number }>;
}

export function getSupportMetrics(): Promise<SupportMetrics> {
  return apiClient
    .get<SupportMetrics>("/platform/support/metrics")
    .then((res) => res.data);
}

export function getSupportInbox(
  status?: SupportConversationStatus
): Promise<SupportInboxItem[]> {
  const query = status ? `?status=${status}` : "";
  return apiClient
    .get<SupportInboxItem[]>(`/platform/support/conversations${query}`)
    .then((res) => res.data);
}

export interface AgentSupportThread {
  messages: SupportMessage[];
  /** When the customer last read; null if never, and always null for a visitor. */
  customerLastReadAt: string | null;
}

export function getAgentSupportMessages(
  conversationId: string,
  before?: string
): Promise<AgentSupportThread> {
  const query = before ? `?before=${encodeURIComponent(before)}` : "";
  return apiClient
    .get<AgentSupportThread>(
      `/platform/support/conversations/${conversationId}/messages${query}`
    )
    .then((res) => res.data);
}

export function postAgentSupportMessage(
  conversationId: string,
  body: string,
  internalNote: boolean
): Promise<SupportMessage> {
  return apiClient
    .post<SupportMessage>(
      `/platform/support/conversations/${conversationId}/messages`,
      { body, internalNote }
    )
    .then((res) => res.data);
}

export interface SupportTenantContext {
  tenantId: string;
  name: string;
  legalName: string | null;
  rut: string;
  planId: string | null;
  planResolvedAt: string | null;
  activityStartDate: string | null;
  literalEVerified: boolean | null;
  fiscalProfile: {
    selfDeclared: true;
    companyType: string | null;
    ivaRegime: string | null;
    ivaMinimumBenefit: string | null;
    fiscalBenefitOptIn: boolean | null;
  } | null;
  benefitDirection:
    | "art5_provider_discount"
    | "art4_client_self_credit"
    /** Declared NOT to meet the requisitos — a known fact, not missing data. */
    | "none_declined"
    | null;
  benefitDirectionReason: string;
  /** Tailored to this tenant's benefit direction; empty when unknown. */
  suggestedReplies: Array<{ id: string; label: string; body: string }>;
}

/** Null when the conversation belongs to an anonymous visitor. */
export interface SupportVisitorContext {
  name: string | null;
  email: string | null;
  firstSeenAt: string;
  /** Set once they registered with this email — no longer a prospect. */
  linkedUserId: string | null;
}

export interface SupportConversationContext {
  tenant: SupportTenantContext | null;
  visitor: SupportVisitorContext | null;
}

export function getSupportContext(
  conversationId: string
): Promise<SupportConversationContext> {
  return apiClient
    .get<SupportConversationContext>(
      `/platform/support/conversations/${conversationId}/context`
    )
    .then((res) => res.data);
}

/**
 * Stamp the operator's read state. The console never called this, so
 * agent lastReadAt was never recorded and nothing could be built on it.
 */
export function markAgentSupportConversationRead(
  conversationId: string
): Promise<void> {
  return apiClient
    .post(`/platform/support/conversations/${conversationId}/read`)
    .then(() => undefined);
}

export function setSupportConversationStatus(
  conversationId: string,
  status: SupportConversationStatus
): Promise<void> {
  return apiClient
    .patch(`/platform/support/conversations/${conversationId}/status`, {
      status,
    })
    .then(() => undefined);
}

export function assignSupportConversationToMe(
  conversationId: string
): Promise<void> {
  return apiClient
    .post(`/platform/support/conversations/${conversationId}/assign-to-me`)
    .then(() => undefined);
}

/* ------------------------------------------------------------------ socket */

/**
 * URL for the live-delta socket. The token rides on the query string
 * because the browser WebSocket API cannot set an Authorization header —
 * the gateway reads it off the upgrade request for exactly this reason.
 */
export function supportSocketUrl(): string | null {
  const token = getToken();
  if (!token) {
    return null;
  }
  const base = apiBaseUrl.replace(/^http/, "ws");
  return `${base}/ws/support?token=${encodeURIComponent(token)}`;
}
